import { PaymentProcessor } from '@rbilabs/common';

import { getCurrentSession } from 'remote/auth';
import { encryptCredit } from 'utils/encryption';
import { TokenExPublicKey, getConfigValue, thDigitalUrl } from 'utils/environment';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';

import {
  LOG_MESSAGE_ADD_CREDIT_ACCOUNT_ATTEMPT,
  LOG_MESSAGE_ADD_CREDIT_ACCOUNT_FAILED,
  RBIPaymentError,
} from './constants';
import { ITHDigitalRequestBody, ITokenizeCard } from './types';

export const tokenizeCard: ITokenizeCard = async (
  reshapedPaymentValues,
  enableDirectPay,
  user,
  logger
) => {
  if (!enableDirectPay || !user) {
    return null;
  }

  const logParams = {
    paymentProcessor: PaymentProcessor.CHASE,
    cardType: null,
  };

  const publicKey = getConfigValue('tokenEx')?.publicKey ?? TokenExPublicKey;

  try {
    // CC details encryption for TokenEx
    const encryptedCredit = await encryptCredit({
      credit: reshapedPaymentValues,
      fdPublicKey: publicKey,
      isUsingTokenEx: true,
    });
    const session = await getCurrentSession();
    const billingCountry = user.details?.isoCountryCode;
    const { countryCode } = getCountryAndCurrencyCodes(billingCountry as ISOs);
    const { cardNumber, expiryDate, securityCode, cardType } = encryptedCredit;

    const url = getConfigValue('urls')?.thDigital ?? thDigitalUrl;

    logParams.cardType = cardType;

    const headers = {
      Authorization: `Bearer ${session?.getIdToken().getJwtToken()}`,
      'Content-Type': 'application/vnd.rbi.add-funding-card-direct-pay.v2+json',
    };

    const body: ITHDigitalRequestBody = {
      creditCard: {
        cardNumber,
        securityCode,
        expirationMonth: expiryDate?.month,
        expirationYear: expiryDate?.year,
        cardType,
        billing: {
          countryCode,
        },
      },
    };
    // Post request to get a chaseProfileId
    const response = await fetch(`${url}/v0/directpay`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (response.ok) {
      const { chaseProfileId } = await response.json();
      if (chaseProfileId) {
        return chaseProfileId;
      }
    }
    throw new Error('Error tokening card');
  } catch (err) {
    /**
     * 2 reasons this looks strange:
     * 1. log levels lower than 'error' are not sent to DD, so log attempt as an error ensuring it goes to DD
     * so we can track it.
     * 2. Logging the attempt and failure back-to-back is unusual, but these need to be combined. Our backend
     * traditionally logs credit card vaulting attempts, success, and failures so that they can be displayed on a
     * dashboard as percentages. However, this TH-specific tokenization method doesn't touch our backend when it fails,
     * so we have to do some logging here in FE to ensure all failures are tracked.
     *
     * If the attempt were logged before making the request, then a successful card vault would have attempts logged
     * here AND on the backend, throwing off our numbers in the dashboard. With this solution, every tokenization
     * failure in the FE has a corresponding attempt logged, and every success in the FE has logging handled 100% by
     * the backend without any duplication between FE and BE. This keeps our numbers accurate.
     */

    logger.error({
      ...logParams,
      message: LOG_MESSAGE_ADD_CREDIT_ACCOUNT_ATTEMPT,
      bypassSampling: true,
    });
    logger.error({
      ...logParams,
      reason: RBIPaymentError.TOKENIZATION_FAILURE,
      message: LOG_MESSAGE_ADD_CREDIT_ACCOUNT_FAILED,
      bypassSampling: true,
    });

    throw err;
  }
};
